import { TeamName } from "@/components/TeamName";
import { type WorkOrderWidgetList } from "@repo/api";
import {
  Button,
  ListView,
  ListViewItem,
  Text,
  calendarIcon,
  personIcon,
  warehouseIcon,
} from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  CoWorkerName,
  LabelField,
  ModalSheet,
  SectionContainer,
  TwoColumnSectionContainer,
  localizeDateAndTime,
} from "@repo/ui";
import { useNavigate } from "react-router-dom";

interface ViewWorkOrderDetailsModalProps {
  isOpen: boolean;
  closeModal: () => void;
  workOrder?: WorkOrderWidgetList;
}

export const ViewWorkOrderDetailsModal = ({
  isOpen,
  closeModal,
  workOrder,
}: ViewWorkOrderDetailsModalProps) => {
  const navigate = useNavigate();
  const { dateFnsLocale } = useTranslation();
  if (!workOrder) {
    return null;
  }
  return (
    <ModalSheet
      title={`${t("label.work_order")} - ${workOrder?.workOrderNumber}`}
      visible={isOpen}
      onClose={closeModal}
      footer={
        <Button
          type="primary"
          text={t("action.open_project")}
          onClick={() => navigate(`/opportunity/${workOrder?.projectId}`)}
        />
      }
    >
      <SectionContainer>
        {workOrder?.projectName && (
          <Text textStyle="Heading.M">{workOrder?.projectName}</Text>
        )}
        <ListView id="installation-quotation-details" showDivider={false}>
          <ListViewItem
            title={t("label.owner")}
            description={
              workOrder.projectAssignedTo ? (
                <CoWorkerName email={workOrder.projectAssignedTo} />
              ) : (
                <TeamName teamId={Number(workOrder.projectAssignedTeam)} />
              )
            }
            leadingIcon={personIcon}
          />
          <ListViewItem
            leadingIcon={warehouseIcon}
            title={t("label.cmp")}
            description={workOrder.buName ?? workOrder.buCode ?? "-"}
          />
          <ListViewItem
            title={t("label.customer")}
            description={
              workOrder.customerFirstName || workOrder.customerLastName
                ? `${workOrder.customerFirstName ?? ""} ${workOrder.customerLastName ?? ""}`
                : "-"
            }
            leadingIcon={personIcon}
          />
          <ListViewItem
            leadingIcon={calendarIcon}
            title={t("label.installation_start_date")}
            description={localizeDateAndTime(
              workOrder.scheduledStartTime ?? "",
              dateFnsLocale
            )}
          />
        </ListView>
        <TwoColumnSectionContainer>
          <LabelField
            id="id"
            label={t("label.quote_external_id")}
            value={workOrder.workOrderExternalId ?? "-"}
          />
          <LabelField
            id="id"
            label={t("label.opportunity_external_id")}
            value={workOrder.opportunityExternalId ?? "-"}
          />
        </TwoColumnSectionContainer>
        <TwoColumnSectionContainer>
          <LabelField
            id="id"
            label={t("label.deposit_paid")}
            value={
              workOrder.measurementFilesUploaded
                ? t("label.yes")
                : t("label.no")
            }
          />
        </TwoColumnSectionContainer>
        <TwoColumnSectionContainer>
          <LabelField
            id="id"
            label={t("label.quote_number")}
            value={workOrder.workOrderNumber ?? "-"}
          />
          <LabelField
            id="id"
            label={t("label.status")}
            value={workOrder.status ?? "-"}
          />
        </TwoColumnSectionContainer>
      </SectionContainer>
    </ModalSheet>
  );
};
